import { createFileRoute, Link, redirect, useNavigate, useSearch } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { z } from 'zod'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FormField } from '@/components/ui/form-field'
import { meQuery, useLoginMutation } from '@/api/auth'
import { ApiError } from '@/api/errors'

/**
 * /login — public route.
 *
 * If a session already exists, redirect away (no point showing login to
 * a logged-in user). Honour `?from=` to bounce back to where they came.
 *
 * Form is RHF + Zod. The mutation fires `POST /api/auth/login`; on
 * success we invalidate `meQuery` and navigate.
 */

const loginSearchSchema = z.object({
  from: z.string().optional(),
})

const loginFormSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
})

type LoginForm = z.infer<typeof loginFormSchema>

export const Route = createFileRoute('/login')({
  validateSearch: loginSearchSchema,
  beforeLoad: async ({ context, search }) => {
    // If already signed in, bounce.
    try {
      await context.queryClient.ensureQueryData(meQuery())
      throw redirect({ to: search.from ?? '/dashboard' })
    } catch (err) {
      // ApiError 401 → fine, stay on login
      if (err instanceof ApiError && err.isUnauthorized) return
      // redirect() throws to redirect — let it through
      throw err
    }
  },
  component: LoginPage,
})

function LoginPage() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/login' })
  const login = useLoginMutation()
  const form = useForm<LoginForm>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: { email: '', password: '' },
  })

  const onSubmit = (values: LoginForm) => {
    login.mutate(values, {
      onSuccess: () => {
        void navigate({ to: search.from ?? '/dashboard' })
      },
      onError: (err) => {
        if (err instanceof ApiError) {
          if (err.code === 'invalid_credentials') {
            form.setError('password', { message: 'Email or password is incorrect' })
            return
          }
          if (err.fields) {
            for (const [field, message] of Object.entries(err.fields)) {
              if (field === 'email' || field === 'password') {
                form.setError(field as keyof LoginForm, { message })
              }
            }
            return
          }
        }
        toast.error(err.message || 'Sign-in failed')
      },
    })
  }

  return (
    <div className="grid min-h-screen place-items-center bg-surface-muted px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <img src="/fuku-logo.svg" alt="Fuku AI" width={80} height={26} className="h-12 w-auto" />
          <h1 className="text-2xl font-bold tracking-tight text-ink">Sign in to Fuku AI</h1>
          <p className="text-sm text-muted">Continue your job search automation.</p>
        </div>

        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-4 rounded-[var(--radius-card)] border border-divider bg-surface-base p-6 shadow-card"
        >
          <FormField
            label="Email"
            htmlFor="email"
            error={form.formState.errors.email?.message}
          >
            <Input
              id="email"
              type="email"
              autoComplete="email"
              aria-invalid={!!form.formState.errors.email}
              {...form.register('email')}
            />
          </FormField>

          <FormField
            label="Password"
            htmlFor="password"
            error={form.formState.errors.password?.message}
          >
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              aria-invalid={!!form.formState.errors.password}
              {...form.register('password')}
            />
          </FormField>

          <div className="-mt-2 text-right">
            <Link to="/forgot-password" className="text-sm font-medium text-primary hover:underline">
              Forgot password?
            </Link>
          </div>

          <Button
            type="submit"
            disabled={login.isPending}
            aria-busy={login.isPending}
            className="mt-2"
          >
            {login.isPending ? (
              <>
                <Loader2 className="animate-spin" aria-hidden="true" />
                Signing in…
              </>
            ) : (
              'Sign in'
            )}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-muted">
          Don&apos;t have an account?{' '}
          <Link to="/signup" className="font-medium text-primary hover:underline">
            Create one
          </Link>
        </p>
      </div>
    </div>
  )
}
